import { LaunchdarklyService } from "@acesso-io/launchdarkly-client-ts/lib/launchdarkly";
import * as ld from 'launchdarkly-js-client-sdk';
import mixpanel from "mixpanel-browser";

import { Environment } from "../../interfaces/environment/environment.interface";
import { getCurrentOrganization, getCurrentUser } from "../storage/storage.service";

declare const environment: Environment;

const LAUNCH_DARKLY_SHARED_KEY = '00000000-0000-0000-0000-000000000000';

function getStoredUser() {
  const user = getCurrentUser();

  if (user) {
    return user
  }
  return null;
}

/**
 * Initializes the LaunchDarkly service with the organization key if it exists in the local storage.
 * Otherwise it initializes the service with a context identified by a shared key.
 * @param launchDarklyService LaunchDarkly service
 * @returns Promise
 */
function initializeLaunchDarkly(launchDarklyService: LaunchdarklyService) {
  const organization = getCurrentOrganization().organization;
  if (organization) {
    const user = getStoredUser();

    const identifiedContext = {
      kind: 'multi',
      organization: {
        key: organization,
      },
      user: {
        appName: 'dashboard',
        key: user.user_id,
      },
    } as ld.LDMultiKindContext;

    return launchDarklyService.init({ context: identifiedContext });
  }

  const sharedContext = {
    kind: 'user',
    key: LAUNCH_DARKLY_SHARED_KEY,
    appName: 'dashboard',
  } as ld.LDSingleKindContext;

  return launchDarklyService.init({ context: sharedContext });
}

/**
 * Initializes Mixpanel without automatic page views: an anonymous `$mp_web_page_view` on every
 * page load turns each visit to a pre-login screen into a billable Monthly Tracked User.
 * Page views are tracked explicitly per route in AppComponent instead.
 *
 * When a session is already authenticated, the stored user is re-identified right away so the
 * device keeps resolving to the same Mixpanel identity even if its local storage was wiped.
 */
function initializeMixpanel() {
  mixpanel.init(
    environment.mixpanelToken,
    { debug: !environment.production, persistence: 'localStorage' }
  );

  const user = getStoredUser();
  if (user?.user_id) {
    mixpanel.identify(user.user_id);
  }
}

export default function initializeAppFactory(launchDarklyService: LaunchdarklyService) {
  return async () => {
    await initializeLaunchDarkly(launchDarklyService);

    initializeMixpanel();
  };
}
